import { ZCLUB_API_URL } from '../config/api';
import { makeAuthenticatedApiCall } from './authApi';

const socialBannerURL = (path = '', query = {}) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  const qs = params.toString();
  return `${ZCLUB_API_URL}/social-banner${path}${qs ? `?${qs}` : ''}`;
};

/**
 * Club Social Banner list.
 * Replace the demo return with GET /api/zclub/social-banners when module is ready.
 * Expected item: { id, description, image_landscape, image_portrait, share_url, link, liked_by_me, like_count, comments }
 */
export async function getSocialBanners(filters = {}) {
  const result = await makeAuthenticatedApiCall(
    socialBannerURL('', {
      page: filters.page || 1,
      limit: filters.limit || 10,
      search: filters.search || '',
    }),
    { method: 'GET' },
  );

  if( result.status === 'success' && result.data && result.data.items ) {
    return result?.data?.items.map(item => formatBannerForDisplay(item));
  }

  return [];
}

export async function getLikes(bannerId) {
  const result = await makeAuthenticatedApiCall(
      socialBannerURL(`/likes/${bannerId}`),
      { method: 'GET' },
    );

  if( result.status === 'success' && result.data && result.data.items ) {
    return result?.data?.items.map(item => formatLikeForDisplay(item));
  }

  return [];
}

export async function getComments(bannerId) {
  const result = await makeAuthenticatedApiCall(
      socialBannerURL(`/comments/${bannerId}`),
      { method: 'GET' },
    );

  if( result.status === 'success' && result.data && result.data.items ) {
    return result?.data?.items.map(item => formatCommentForDisplay(item));
  }

  return [];
}

export async function toggleLike(bannerId) {
  const result = await makeAuthenticatedApiCall(
    socialBannerURL(`/like/${bannerId}`),
    { method: 'POST' },
  );

  if( result.status === 'success' && result.data ) {
    return {
      liked: result.data?.liked || false,
      likeCount: result.data?.total_like || result.data?.like_count || result.data?.likeCount || 0,
    }
  }

  return false;
}

export async function submitComment(bannerId, commentId, comment) {
  const result = await makeAuthenticatedApiCall(
    socialBannerURL(`/comment/${bannerId}`),
    { 
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        'comment_id': commentId || null,
        'comment': comment,
      })
    },
  );

  if( result.status === 'success' && result.data ) {
    return {
      comment: formatCommentForDisplay(result.data.comment),
      commentCount: result.data?.total_comment || 0,
    };
  }

  return false;
}

export async function deleteComment(commentId) {
  const result = await makeAuthenticatedApiCall(
    socialBannerURL(`/comment/${commentId}`),
    { 
      method: 'DELETE',
    },
  );

  if( result.status === 'success' && result.data ) {
    return {
      commentCount: result.data?.total_comment || 0,
    };
  }

  return false;
}

export async function likeComment(commentId) {
  const result = await makeAuthenticatedApiCall(
    socialBannerURL(`/comment/like/${commentId}`),
    { 
      method: 'POST',
    },
  );

  if( result.status === 'success' && result.data ) {
    return {
      likers: result.data?.likers || [],
    };
  }

  return false;
}

export async function submitShare(bannerId, via) {
  const result = await makeAuthenticatedApiCall(
    socialBannerURL(`/share/${bannerId}`),
    { 
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ via }),
    },
  );

  if( result.status === 'success' && result.data ) {
    return {
      url: result.data?.url || '',
    };
  }

  return false;
}

export async function submitShareEmail(bannerId, email) {
  const result = await makeAuthenticatedApiCall(
    socialBannerURL(`/email/${bannerId}`),
    { 
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        'email': email,
      }),
    },
  );

  return {
    success: result.status === 'success',
    message: result.status || '',
  };
}

export function formatBannerForDisplay(banner) {
  return {
    id: banner.id,
    description: banner.description,
    image_landscape: banner.primary_image,
    image_portrait: banner.secondary_image,
    link: banner.external_link,
    view_count: banner.total_view,
    like_count: banner.total_like,
    comment_count: banner.total_comment,
    share_count: banner.total_shares,
    liked_by_me: banner.liked,
    share_url: banner.url,
    comments: (banner.comments || []).map(comment => formatCommentForDisplay(comment)),
    comment_displayed: banner.comment_displayed || false,
  }
}

export function formatCommentForDisplay(comment) {
  if(!comment) return null;

  return {
    id: comment.id,
    parentId: comment.id_comment,
    userId: comment.id_user,
    user: comment.user_name,
    avatar: comment.avatar,
    text: comment.comment,
    time: '2j',
    timestamp: comment.timestamp,
    mention: comment.mentioned,
    likers: comment.likers || [],
    replies: (comment.replies || []).map(reply => formatCommentForDisplay(reply)),
  }
}

export function formatLikeForDisplay(like) {
  if(!like) return null;

  return {
    id: like.id,
    user: like.user_name,
    avatar: like.avatar,
  }
}